import type {
  Difficulty, GenContext, Question, QuestionFamily, QuestionKind, RubricCriterion, Stimulus, SubjectId,
} from '@/core/types';
import {
  categorize, choice, forSkill, matching, multi, ordering, rubric, steps, text, tfReason, tokens,
} from './build';
import { hashString } from '@/core/util/rng';
import { L, getLang, resolveBi, type DeepBi } from '@/i18n';

/**
 * Bank-driven question families for content subjects. Two sources of variety:
 *  1. Authored items (scenario, evaluate-reasoning, open response …)
 *  2. Structured concept tables, from which matching / categorisation /
 *     identification / ordering tasks are composed combinatorially.
 */

type Distractor = string | [string, string];

export interface AuthoredItem {
  id: string;
  d: Difficulty;
  kind: QuestionKind;
  prompt: string;
  context?: string;
  stimulus?: Stimulus;
  correct?: string;
  wrong?: Distractor[];
  multi?: { correct: string[]; wrong: Distractor[] };
  tf?: { truth: boolean; reason: string; wrong: Distractor[] };
  text?: string[];
  order?: string[];
  cats?: { items: [string, string][]; categories: string[] };
  match?: [string, string][];
  tokens?: { sentences: string[]; extra?: string[] };
  rubric?: { criteria: RubricCriterion[]; min: number; exemplar: string };
  steps?: { label: string; value?: number; accepted?: string[] }[];
  hints?: string[];
  solution: string[];
}

export interface ConceptItem {
  term: string;
  meaning: string;
  category?: string;
  /** Concepts sharing a sense group are never used as each other's distractors. */
  sense?: string;
}

export interface BankSpec {
  skillId: string;
  subjectId: SubjectId;
  objective: string;
  concepts?: ConceptItem[];
  categories?: string[];
  /** Items in correct order (timelines, processes). */
  sequence?: { label: string; items: string[] };
  items?: AuthoredItem[];
  termLabel?: string;
  meaningLabel?: string;
  range?: [Difficulty, Difficulty];
  speechLang?: string;
}

/**
 * Any text in a bank may be a bilingual `B(en, nb)` pair; the spec is resolved
 * to the active language when a question is generated (and cached per language).
 */
export function bankFamily(input: DeepBi<BankSpec>): QuestionFamily {
  const cache = new Map<string, BankSpec>();
  const spec = () => {
    const lang = getLang();
    let s = cache.get(lang);
    if (!s) { s = resolveBi<BankSpec>(input); cache.set(lang, s); }
    return s;
  };
  const first = spec();
  const range = first.range ?? [1, 7];
  const kinds = new Set<QuestionKind>(['matching', 'multiple-choice']);
  first.items?.forEach((i) => kinds.add(i.kind));
  if (first.categories) kinds.add('categorization');
  if (first.sequence) kinds.add('ordering');
  const family: QuestionFamily = {
    id: `${first.skillId}#bank`,
    skillId: first.skillId,
    objective: first.objective,
    difficulties: range,
    kinds: [...kinds],
    generate: (ctx) => generateFromBank(spec(), ctx),
  };
  Object.defineProperty(family, 'objective', { get: () => spec().objective, enumerable: true });
  return family;
}

function generateFromBank(spec: BankSpec, ctx: GenContext): Question | null {
  const { rng, difficulty: d } = ctx;
  const Q = forSkill(spec.skillId, spec.subjectId, `${spec.skillId}#bank`);
  const makers: (() => Question | null)[] = [];

  // Authored items near the requested difficulty
  const near = (spec.items ?? []).filter((i) => Math.abs(i.d - d) === 0);
  const close = (spec.items ?? []).filter((i) => Math.abs(i.d - d) === 1);
  const pool = near.length ? near : close;
  for (const item of pool) {
    if (ctx.avoid.has(`${spec.skillId}|item-${item.id}|${hashString(item.prompt)}`)) continue;
    makers.push(() => fromAuthored(spec, ctx, item, Q));
  }

  const concepts = spec.concepts ?? [];
  const tl = spec.termLabel ?? L('term', 'begrep');
  const ml = spec.meaningLabel ?? L('meaning', 'betydning');

  if (concepts.length >= 4) {
    if (d <= 2) {
      makers.push(() => {
        const c = rng.pick(concepts);
        const wrongs = distinct(concepts, c, rng, 3).map((x) => x.meaning);
        const ch = choice(rng, c.meaning, wrongs);
        return Q(ctx, { form: 'term-to-meaning', params: c.term, kind: 'multiple-choice', difficulty: d, ...ch, prompt: L(`What is the ${ml} of **${c.term}**?`, `Hvilken ${ml} passer til **${c.term}**?`), hints: [L(`Think about where you have seen "${c.term}" used.`, `Tenk på hvor du har sett «${c.term}» brukt.`)], solution: [`${c.term}: ${c.meaning}.`], speechLang: spec.speechLang });
      });
      makers.push(() => {
        const c = rng.pick(concepts);
        const wrongs = distinct(concepts, c, rng, 3).map((x) => x.term);
        const ch = choice(rng, c.term, wrongs);
        return Q(ctx, { form: 'meaning-to-term', params: c.term, kind: 'multiple-choice', difficulty: d, ...ch, prompt: L(`Which ${tl} matches: "${c.meaning}"?`, `Hva passer til: «${c.meaning}»?`), hints: [L('Eliminate options you know are wrong.', 'Stryk alternativene du vet er feil.')], solution: [`${c.meaning} → ${c.term}.`], speechLang: spec.speechLang });
      });
    }
    if (d >= 2 && d <= 4) {
      makers.push(() => {
        const n = d === 2 ? 3 : d === 3 ? 4 : 5;
        const picked = uniqueBySense(rng.shuffle(concepts), n);
        if (picked.length < 3) return null;
        return Q(ctx, { form: `match-${n}`, params: picked.map((p) => p.term).sort().join('|'), kind: 'matching', difficulty: d, ...matching(rng, picked.map((p) => [p.term, p.meaning])), prompt: L(`Match each ${tl} with its ${ml}.`, `Koble sammen ${tl} og ${ml} som hører sammen.`), hints: [L('Start with the pairs you are sure about.', 'Begynn med parene du er sikker på.')], solution: picked.map((p) => `${p.term} — ${p.meaning}`), speechLang: spec.speechLang });
      });
    }
    if (spec.categories && d >= 3) {
      makers.push(() => {
        const n = d <= 3 ? 4 : d <= 5 ? 6 : 7;
        const withCat = concepts.filter((c) => c.category && spec.categories!.includes(c.category));
        const picked = rng.sample(withCat, Math.min(n, withCat.length));
        const usedCats = spec.categories!.filter((cat) => picked.some((p) => p.category === cat));
        if (usedCats.length < 2) return null;
        return Q(ctx, { form: `categorize-${n}`, params: picked.map((p) => p.term).sort().join('|'), kind: 'categorization', difficulty: d, ...categorize(rng, picked.map((p) => [p.term, p.category!]), usedCats), prompt: L(`Sort each ${tl} into the right category.`, 'Sorter i riktig kategori.'), hints: [L('Use what each item means, not how it sounds.', 'Tenk på hva hver ting betyr, ikke hvordan den høres ut.')], solution: picked.map((p) => `${p.term} → ${p.category}`), speechLang: spec.speechLang });
      });
    }
    if (spec.categories && d >= 4) {
      makers.push(() => {
        const cat = rng.pick(spec.categories!);
        const inCat = concepts.filter((c) => c.category === cat);
        const outCat = concepts.filter((c) => c.category && c.category !== cat);
        if (inCat.length < 2 || outCat.length < 2) return null;
        const corr = rng.sample(inCat, Math.min(inCat.length, rng.int(2, 3)));
        const wr = rng.sample(outCat, Math.min(outCat.length, 3));
        const m = multi(rng, corr.map((c) => c.term), wr.map((c) => c.term));
        return Q(ctx, { form: 'select-all-in-category', params: `${cat}:${corr.map((c) => c.term).sort().join('|')}`, kind: 'multi-select', difficulty: d, ...m, prompt: L(`Select **all** items that belong to: **${cat}**.`, `Velg **alle** som hører til: **${cat}**.`), hints: [L('Several options may be correct.', 'Flere alternativer kan være riktige.')], solution: [`${cat}: ${corr.map((c) => c.term).join(', ')}.`, ...wr.map((w) => `${w.term} → ${w.category}`)], speechLang: spec.speechLang });
      });
    }
  }

  if (spec.sequence && d >= 2) {
    makers.push(() => {
      const items = spec.sequence!.items;
      const n = Math.min(items.length, d <= 3 ? 4 : d <= 5 ? 5 : items.length);
      // contiguous window keeps the order meaningful
      const start = rng.int(0, items.length - n);
      const slice = items.slice(start, start + n);
      return Q(ctx, { form: `order-${n}`, params: slice.join('|'), kind: 'ordering', difficulty: d, ...ordering(rng, slice), prompt: L(`Put these in the correct order: ${spec.sequence!.label}.`, `Sett i riktig rekkefølge: ${spec.sequence!.label}.`), hints: [L('Find the first and the last, then fill the middle.', 'Finn den første og den siste, og fyll så inn midten.')], solution: [slice.join(' → ')] });
    });
  }

  const usable = makers.length ? makers : (spec.items ?? []).map((item) => () => fromAuthored(spec, ctx, item, Q));
  const shuffled = rng.shuffle(usable);
  for (const m of shuffled) {
    const q = m();
    if (q && !ctx.avoid.has(q.fingerprint)) return q;
  }
  const any = shuffled[0]?.();
  return any ?? null;
}

function distinct(concepts: ConceptItem[], target: ConceptItem, rng: GenContext['rng'], n: number): ConceptItem[] {
  const sameCat = concepts.filter((c) => c !== target && c.meaning !== target.meaning && (!target.sense || c.sense !== target.sense) && c.category === target.category);
  const other = concepts.filter((c) => c !== target && c.meaning !== target.meaning && (!target.sense || c.sense !== target.sense) && c.category !== target.category);
  return [...rng.shuffle(sameCat), ...rng.shuffle(other)].slice(0, n);
}

function uniqueBySense(items: ConceptItem[], n: number): ConceptItem[] {
  const out: ConceptItem[] = [];
  const senses = new Set<string>();
  const meanings = new Set<string>();
  for (const c of items) {
    if (out.length >= n) break;
    const s = c.sense ?? c.term;
    if (senses.has(s) || meanings.has(c.meaning)) continue;
    senses.add(s);
    meanings.add(c.meaning);
    out.push(c);
  }
  return out;
}

function fromAuthored(spec: BankSpec, ctx: GenContext, item: AuthoredItem, Q: ReturnType<typeof forSkill>): Question | null {
  const { rng } = ctx;
  const base = {
    form: `item-${item.id}`,
    params: String(hashString(item.prompt)),
    kind: item.kind,
    difficulty: item.d,
    prompt: item.prompt,
    context: item.context,
    stimulus: item.stimulus,
    hints: item.hints,
    solution: item.solution,
    provenance: 'AUTHORED_DEMO' as const,
    speechLang: spec.speechLang,
  };
  if (item.correct && item.wrong) return Q(ctx, { ...base, ...choice(rng, item.correct, item.wrong) });
  if (item.multi) return Q(ctx, { ...base, ...multi(rng, item.multi.correct, item.multi.wrong) });
  if (item.tf) return Q(ctx, { ...base, ...tfReason(rng, item.tf.truth, item.tf.reason, item.tf.wrong) });
  if (item.text) return Q(ctx, { ...base, ...text(item.text) });
  if (item.order) return Q(ctx, { ...base, ...ordering(rng, item.order) });
  if (item.cats) return Q(ctx, { ...base, ...categorize(rng, item.cats.items, item.cats.categories) });
  if (item.match) return Q(ctx, { ...base, ...matching(rng, item.match) });
  if (item.tokens) return Q(ctx, { ...base, ...tokens(rng, item.tokens.sentences, item.tokens.extra) });
  if (item.rubric) return Q(ctx, { ...base, ...rubric(item.rubric.criteria, item.rubric.min, item.rubric.exemplar, L('Write your answer in your own words…', 'Skriv svaret med dine egne ord …')) });
  if (item.steps) return Q(ctx, { ...base, ...steps(item.steps) });
  return null;
}

/** Rubric helper: crit('cause', 'Explains the cause', ['because', 'due to']) */
export function crit(id: string, label: string, keywords: string[], weight?: number): RubricCriterion;
export function crit(id: string, label: string | { en: string; nb: string }, keywords: string[], weight?: number): DeepBi<RubricCriterion>;
export function crit(id: string, label: string | { en: string; nb: string }, keywords: string[], weight = 1) {
  return { id, label, keywords, weight };
}
